import {cloneDeep} from 'lodash-es';
import {StandbyListService as LegacyStandbyListService} from "app/services/standbyList/StandbyList.service";
import { from, Observable, of, throwError } from "rxjs";
import {
  first,
  switchMap,
  map,
  catchError,
  timeout,
  tap,
  retry,
  mergeMap,
  retryWhen,
  delay,
} from "rxjs/operators";
import BookingUpdateService from "external-services/booking-update-service/index";
import { BookingMethods, IBooking, ICustomer } from 'shared-types/index';
import BookingLookupService from "external-services/booking-lookup-service/index";

/**
 * Purpose of these "Actions" is to mimic a more redux-style of event management, which
 * should make transitioning to redux a little easier when the time comes.
 */

const NS = 'EditBookingActions';

export interface IConfirmStandbyReponse {
  status: number;
  booking: IBooking;
}

export default function EditBookingActions($rootScope: any) {
  "ngInject";

  return {
    deleteBookingConfirmed: (booking: IBooking): Promise<IBooking|boolean> => {
      return BookingUpdateService.setToCancelled(booking);
    },
    deleteBookingCancelled: (booking: IBooking) => {
      $rootScope.$broadcast('edit-booking', null, booking);
    },
    changeBookingDate: (booking: IBooking, customer: ICustomer) => {
      const bookingTime = cloneDeep(booking.time);
      const evt = {};
      $rootScope.$emit('new-booking', evt, bookingTime, null, BookingMethods.phone, booking, customer);
    },
    confirmStandby: (booking: IBooking): Observable<IConfirmStandbyReponse> => {
      // test different responses
      // return of(403);
      // return of(404);
      // return of(400);
      // return of(408);
      // return of(500);

      const TOO_MANY_CODE = 429;
      const TIMEOUT_CODE = 408;
      let timeoutVar = 10000;
      let retryCount = 0;
      return LegacyStandbyListService.upgradeStandbyList(booking)
        .pipe(
          tap(() => {
            retryCount += 1;
            if (retryCount > 1) {
              timeoutVar = 20000;
            }
          }),
          timeout(timeoutVar), // give back end 10 seconds to respond before timing out, change to 20 if failed
          switchMap((status: number) => {
            // waits for back end change to refresh in couch
            return waitForBookingUpdate(booking._id, booking._rev, booking.time)
              .pipe(map((booking: IBooking) => ({status, booking})))
          }),
          /**
           * retry a few times. Sometimes it takes awhile for couch to sync with pouch
           */
          retry(5),
          catchError(err => {
            if (err === TOO_MANY_CODE) {
              return of({status: TOO_MANY_CODE, booking: null}); // too many requests
            }
            return of({status: TIMEOUT_CODE, booking: null}); // timeout
          })
        )
    }
  }

  /**
   * Waits for pouch to trigger an update on the specified booking. This is useful if booking is expected to changed
   * on the back end and you need to wait for that change to refresh to avoid a couch conflict.
   */
  function waitForBookingUpdate(bookingId: string, bookingRev: string, bookingTime: Date): Observable<IBooking> {
    const CONFLICT_CODE = 409;
    return from(BookingLookupService.getById(bookingId, bookingTime) as Promise<IBooking>)
      .pipe(
        first(),
        mergeMap((booking) => booking._rev === bookingRev ? throwError(CONFLICT_CODE) : of(booking)),
        retryWhen(errors => errors.pipe(
          delay(1000), // allow some time between retries
          tap(val => {
            if (val === CONFLICT_CODE) {
              console.warn(NS, `Same '_rev' detected. Ignoring and retrying.`);
              throw CONFLICT_CODE;
            }
          })
        ))
      );
  }
}
